import { DotLegalButton, DotLegalOverflowMenu, ITableHeader } from "@dotlegal/dotlegal-ui-components";
import DotLegalPaper from "../../common/components/dotLegalPaper/DotLegalPaper";
import { LegalEntityContactRow, LegalEntityContactType } from "./LegalEntityContactTab.types";
import { useTranslation } from "../../localization/useTranslation";
import { Box, TableCell, TableRow } from "@mui/material";
import { useLegalEntityContactTabDataMapping } from "./LegalEntityContactTab.hooks";
import CheckIcon from "@mui/icons-material/Check";
import React from "react";
import DeleteLegalEntityContactDialog from "./deleteLegalEntityContactDialog/DeleteLegalEntityContactDialog";
import { DotLegalEmptyState } from "@dotlegal/dotlegal-ui-components";
import NoContacts from "../../processingActivity/processingActivityOverview/processingActivitySharingsTab/noSharings.svg?react";
import { useUserContext } from "../../auth/userContextProvider/UserContextProvider";
import { setFirstLetterToLowerCase } from "../../common/stringOperations";
import { WhiteLockIcon } from "../../common/icons";
import DotLegalTableWithFilterBar from "@common/components/dotLegalTableWithFilterBar/DotLegalTableWithFilterBar";

export interface ILegalEntityContactTab {
    legalEntityId: string | undefined;
    isGroupEntity: boolean;
    onContactsChanged: () => void;
}

function LegalEntityContactTab(props: ILegalEntityContactTab) {
    const { translateString } = useTranslation();
    const { permissions } = useUserContext();
    const {
        data,
        contacts,
        isLoading,
        getFilterOptions,
        onFilterChange,
        pinnedFilterIds,
        onPinnedFilterIdsChange,
        hasActiveFilters,
        getMenuItems,
        openContactPane,
        selectedContact,
        refetchContacts,
        setShowDeleteDialog,
        showDeleteDialog,
        hasContacts,
    } = useLegalEntityContactTabDataMapping(props);

    const hasContactManagementPermissions = () => {
        if (props.isGroupEntity) {
            return permissions.canManageGroupEntities;
        }

        return permissions.companyPermissions.edit;
    };

    return (
        <React.Fragment>
            <DotLegalPaper>
                {hasContacts ? (
                    <DotLegalTableWithFilterBar
                        data={contacts}
                        headers={GetTableHeader(translateString)}
                        defaultOrderBy={"name"}
                        defaultOrder={"asc"}
                        isLoading={isLoading}
                        rowsPerPage={30}
                        filterBar={{
                            isLoading: isLoading,
                            filterData: getFilterOptions(),
                            fetchFilterData: true,
                            onMenuClose: onFilterChange,
                            onRemove: onFilterChange,
                            pinnedFilterIds: pinnedFilterIds,
                            onPinnedFilterIdsChange: onPinnedFilterIdsChange,
                        }}
                        emptyText={translateString(hasActiveFilters ? "noDataMatchingCriteria" : "noData")}
                        clickableRows={false}
                        hasOverflowMenu
                        renderRow={(row, i) => (
                            <TableRow key={row.id}>
                                <TableCell>{row.name}</TableCell>
                                <TableCell>{row.email}</TableCell>
                                <TableCell>{row.phoneNumber}</TableCell>
                                <TableCell>
                                    {row.contactTypes &&
                                        row.contactTypes
                                            .map((x) => translateString(setFirstLetterToLowerCase(LegalEntityContactType[x].toString())))
                                            .join(", ")}
                                </TableCell>
                                <TableCell>{row.primaryContact && <CheckIcon />}</TableCell>
                                <TableCell align={"right"}>{hasContactManagementPermissions() && <DotLegalOverflowMenu items={getMenuItems(row)} />}</TableCell>
                            </TableRow>
                        )}
                    ></DotLegalTableWithFilterBar>
                ) : (
                    <DotLegalEmptyState icon={<NoContacts />} text={translateString("noContacts")} />
                )}
                {hasContactManagementPermissions() && (
                    <Box sx={{ display: "flex", justifyContent: "end", mt: 2 }}>
                        <DotLegalButton
                            buttonType={"primary"}
                            onClick={() => openContactPane()}
                            disabled={data?.isMaxNumberOfContactsReached || isLoading}
                            endIcon={data?.isMaxNumberOfContactsReached ? WhiteLockIcon : undefined}
                            toolTip={data?.isMaxNumberOfContactsReached ? translateString("upgradeDataProtectionPlanOrVendorManagementPlan") : undefined}
                        >
                            {translateString("createNew")}
                        </DotLegalButton>
                    </Box>
                )}
            </DotLegalPaper>
            {showDeleteDialog && (
                <DeleteLegalEntityContactDialog
                    onCloseDialog={() => setShowDeleteDialog(false)}
                    onDeleted={() => {
                        refetchContacts();
                        setShowDeleteDialog(false);
                    }}
                    contactId={selectedContact!.id}
                    contactName={selectedContact!.name}
                />
            )}
        </React.Fragment>
    );
}

export default LegalEntityContactTab;

function GetTableHeader(translateString: any) {
    const headers = Array<ITableHeader<LegalEntityContactRow>>();
    headers.push({ property: "name", text: translateString("name"), align: "left", showOnMobile: true });
    headers.push({ property: "email", text: translateString("email"), align: "left", showOnMobile: true });
    headers.push({ property: "phoneNumber", text: translateString("phone"), align: "left", showOnMobile: true });
    headers.push({ property: "contactTypes", text: translateString("type"), align: "left", showOnMobile: true });
    headers.push({ property: "primaryContact", text: translateString("primaryContact"), align: "left", showOnMobile: true });
    headers.push({ property: "id", text: "", align: "left", showOnMobile: true, hideHeader: true });
    return headers;
}
